import { Box, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { IRecordExpiredFurtherEducationListItem, RecordExpiredFurtherEducationListItem } from "../../../models/record/recordExpiredFurtherEducationListItem";
import * as attachmentService from "../../../models/services/attachmentService";
import * as recordService from "../../../models/services/recordService";
import { List } from "../../framework/List/List";
import { EllipsisTypography } from "../../framework/Typography/EllipsisTypography";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { RecordListFirstColumn } from "../RecordList/RecordListFirstColumn";

export interface IRecordExpiredFurtherEducationsListProps {
    classes?: string;
    companyId: string;
    isReport: boolean;
    employeeTypes: number[];
    jobTitles: string[];
    unitIds: string[];
    activityState: number;
    selectedId?: string;
    checkedIds?: string[];
    dataId?: string;
    sortColumn?: string;
    sortOrderIsAsc?: boolean;
    onSetSelectedIds?: (selectedId: string, checkedIds: string[]) => void;
    onSetSortData?: (sortColumn: string, sortOrderIsAsc: boolean) => void;
}

export interface IRecordExpiredFurtherEducationsListState {
    //List
    intialized: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: IRecordExpiredFurtherEducationListItem[];
}

export class RecordExpiredFurtherEducationsList extends React.Component<IRecordExpiredFurtherEducationsListProps, IRecordExpiredFurtherEducationsListState> {
    constructor(props: IRecordExpiredFurtherEducationsListProps) {
        super(props);
        this.state = {
            //List
            intialized: false,
            isLoading: false,
            pageSize: 10000,
            page: 1,
            hasMore: true,
            filter: "",
            sortColumn: props.sortColumn ? props.sortColumn : "name",
            sortOrderIsAsc: props.sortColumn ? props.sortOrderIsAsc : true,
            items: []
        };
    }

    searchItems = async (pageSize: number, page: number, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, initialLoad: boolean = false) => {
        const props = this.props;
        const state = this.state;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => recordService.getExpiredRecordUserFurtherEducationListItems(props.companyId, props.employeeTypes, props.jobTitles, props.unitIds, props.activityState, !refreshList ? pageSize : (page * 10000),
            !refreshList ? page : 1, sortColumn, sortOrderIsAsc, props.isReport), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(state.items, props.selectedId ?? "", props.checkedIds ?? [], result.items, resetItems, refreshList);
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            sortColumn,
            sortOrderIsAsc,
            items: items.items
        });
        if (props.onSetSelectedIds) {
            props.onSetSelectedIds(items.selectedId, !initialLoad ? items.checkedIds : items.items.map(i => i.id));
        }
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(state.pageSize, 1, state.sortColumn, state.sortOrderIsAsc, true, false, true);
    }

    componentDidUpdate(prevProps: IRecordExpiredFurtherEducationsListProps, prevState: IRecordExpiredFurtherEducationsListState) {
        const props = this.props;
        const state = this.state;
        const prevDataId = prevProps.dataId ? prevProps.dataId : "";
        const dataId = props.dataId ? props.dataId : "";
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const prevJobTitles = prevProps.jobTitles ? prevProps.jobTitles.filter(i => !!i) : [];
        const jobTitles = props.jobTitles ? props.jobTitles.filter(i => !!i) : [];
        const prevActivityState = prevProps.activityState ? prevProps.activityState : 0;
        const activityState = props.activityState ? props.activityState : 0;
        const prevSortColumn = prevProps.sortColumn ? prevProps.sortColumn : state.sortColumn;
        const sortColumn = props.sortColumn ? props.sortColumn : state.sortColumn;
        const prevSortOrderIsAsc = prevProps.sortColumn ? prevProps.sortOrderIsAsc : state.sortOrderIsAsc;
        const sortOrderIsAsc = props.sortColumn ? props.sortOrderIsAsc : state.sortOrderIsAsc;
        if (dataId === prevDataId && companyId === prevCompanyId &&
            Base.equalArrays(prevProps.employeeTypes, props.employeeTypes) &&
            Base.equalStrArrays(prevJobTitles, jobTitles) &&
            Base.equalStrArrays(prevProps.unitIds, props.unitIds) &&
            prevActivityState === activityState &&
            prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
        this.searchItems(state.pageSize, 1, sortColumn, sortOrderIsAsc, true, false, true);
    }

    handleSearchLinesDialog = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, sortColumn, sortOrderIsAsc, !nextPage, false);
    };
        
    filteredList = (column: string, asc: boolean) => {
        const props = this.props;
        const items = this.state.items.slice(0);
        RecordExpiredFurtherEducationListItem.sortRecordExpiredFurtherEducationListItemsByColumn(items, column, asc);
        if (props.onSetSortData) {
            props.onSetSortData(column, asc);
        }
        this.setState({
            sortColumn: column,
            sortOrderIsAsc: asc,
            items
        });
    };
    
    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        this.filteredList(sortColumn, sortOrderIsAsc);
    };

    handleDownloadAttachment = async (id: string) => {
        if (!id) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.intialized) return null;
        return (
            <List
                multiSelect={!props.isReport}
                intialized={state.intialized}
                isLoading={state.isLoading}
                hasMore={state.hasMore}
                height={props.isReport ? "calc(100vh - 160px)" : "100%"}
                header={{
                    columns: [
                        { size: 3, column: "name", content: Translations.FurtherEducation },
                        { size: 2, column: "validityEndDate", content: Translations.ValidityEndDate },
                        { size: 2, column: "lastName", content: Translations.LastName },
                        { size: 2, column: "firstName", content: Translations.FirstName },
                        { size: 1, column: "birthDate", content: Translations.BirthDate },
                        { size: 2, column: "attachments", content: Translations.Attachments }
                    ],
                    defaultChecked: true
                }}
                lines={
                    state.items.map((item) => {
                        return {
                            classes: item.recordArchived ? " disabled" : "",
                            columns: [
                                { size: 3, content: <RecordListFirstColumn item={item} title={item.name} /> },
                                { size: 2, content: Base.utcTimeToDateStr(item.validityEndDate) },
                                {
                                    size: 2,
                                    content:
                                        <Box display="flex" alignItems="center">
                                            <AvatarImage
                                                id={item.recordUserCode}
                                                name={item.getName()}
                                                image={item.recordPicture}
                                                tooltip={item.getName()}
                                                marginRight=".5rem"
                                            />
                                            <Typography>{item.recordLastName}</Typography>
                                        </Box>
                                },
                                { size: 2, content: item.recordFirstName },
                                { size: 1, content: Base.utcTimeToDateStr(item.recordBirthDate) },
                                {
                                    size: 2,
                                    content:
                                            <EllipsisTypography title={Base.getStringWithSeparators(item.attachments.map(i => i.title), ", ")}>
                                                {item.attachments.map(i => (
                                                    <ListLinkValue
                                                        key={i.id}
                                                        onClick={() => this.handleDownloadAttachment(i.id)}
                                                        sx={(theme) => ({
                                                            "&::before": {
                                                                content: "\", \"",
                                                            },
                                                            "&:first-of-type": {
                                                                "&::before": {
                                                                    content: "\"\"",
                                                                },
                                                            },
                                                        })}
                                                    >
                                                        {i.title}
                                                    </ListLinkValue>
                                                ))}
                                            </EllipsisTypography>
                                }
                            ],
                            id: item.id
                        };
                    })
                }
                sortColumn={state.sortColumn}
                sortOrderIsAsc={state.sortOrderIsAsc}
                selectedId={props.isReport ? "" : props.selectedId}
                checkedIds={props.isReport ? [] : props.checkedIds}
                onSetSelectedIds={props.onSetSelectedIds}
                onSearchLines={props.isReport ? this.handleSearchLines : this.handleSearchLinesDialog}
            />
        );
    }
}